import React, { forwardRef } from 'react';
import { FormProvider } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ModalContainer as _ModalContainer, ModalExitIcon } from 'components/core';
import { CheckoutProvider, contextStore } from 'checkout/context';
import { ModalButton } from 'shared/modals';
import useStyledTheme from 'shared/hooks/use-styled-theme';
import { useKioskCheckoutV2 } from './use-kiosk-checkout-v2';
import OrderInformation from './order-information';
import OrderItems from './order-items';

type KioskCheckoutModalProps = {
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  onClose: () => void;
  dispensary?: {
    id: string;
    name: string;
    kioskOrderingEnabled: boolean;
    orderTypesConfigV2?: {
      kiosk?: {
        anonymousCheckout?: boolean;
        directedOrders?: boolean;
      };
    };
  };
};

export const KioskCheckoutModal = forwardRef<HTMLDivElement, KioskCheckoutModalProps>((props, ref) => {
  const { containerProps, onClose, dispensary: dispensaryFromProps } = props;
  const { spaces } = useStyledTheme();
  const {
    formMethods,
    loading,
    isAnonymousCheckout,
    setIsAnonymousCheckout,
    isTurnstileEnabled,
    TurnstileComponent,
    handleSubmitOrder,
    onSubmitFormError,
    onCloseModal,
    dispensary,
  } = useKioskCheckoutV2({ onClose, dispensary: dispensaryFromProps });

  if (!dispensary) {
    return (
      <ModalContainer ref={ref} width={850} {...containerProps}>
        Loading...
      </ModalContainer>
    );
  }

  return (
    <ModalContainer ref={ref} {...containerProps}>
      <ModalExitIcon onClick={onCloseModal} />
      <CheckoutProvider value={contextStore}>
        <Container data-testid='kiosk-checkout-modal'>
          <FormProvider {...formMethods}>
            <form onSubmit={(e) => e.preventDefault()}>
              <OrderInformation
                dispensary={dispensary}
                isAnonymousCheckout={isAnonymousCheckout}
                setIsAnonymousCheckout={setIsAnonymousCheckout}
              />

              <SubmitButton
                loading={loading}
                onClick={formMethods.handleSubmit(handleSubmitOrder, onSubmitFormError)}
                width='100%'
                mt='0'
                mb={spaces[4]}
                gap={spaces[2]}
              >
                Submit Order
              </SubmitButton>
              <Disclaimer>
                By submitting this order the customer agrees to our&nbsp;
                <TermsLink href='/terms' target='_BLANK'>
                  Terms
                </TermsLink>
                &nbsp;and to receive automated text message updates. See{' '}
                <TermsLink href='https://dutchie.com/privacy' target='_BLANK'>
                  Privacy Policy
                </TermsLink>
                .
              </Disclaimer>
              {isTurnstileEnabled && <TurnstileComponent />}
            </form>
          </FormProvider>
        </Container>
        <OrderItems dispensary={dispensary} excludePaymentFee />
      </CheckoutProvider>
    </ModalContainer>
  );
});

const ModalContainer = styled(_ModalContainer)`
  ${({ theme: { spaces } }) => css`
    position: relative;
    padding: 0;
    min-width: 640px;
    max-width: 748px;
    max-height: 95vh;
    overflow-y: auto;
    font-size: 20px;
    @media screen and (max-width: 900px) {
      margin: 0 ${spaces[4]};
    }
  `}
`;

const Container = styled.div`
  ${({ theme: { breakpoints, spaces } }) => css`
    border-radius: inherit;
    text-align: left;
    width: 100%;
    padding: ${spaces[9]};

    > form {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    ${breakpoints.down('sm')} {
      max-height: 100vh;
      overflow-y: auto;
    }
  `}
`;

const Disclaimer = styled.p`
  ${({ theme: { colors } }) => css`
    font-size: 16px;
    color: ${colors.grey[60]};
    text-align: center;
  `}
`;

const TermsLink = styled.a`
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  &:active,
  &:focus,
  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled(ModalButton)`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: ${spaces[6]};
    width: 100%;
    text-transform: uppercase;
    height: 64px;
    font-size: 24px;
  `}
`;
